import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Category } from '~app/modules/generic/models';
import { environment } from '~env/environment';
import { CATEGORY_CAROUSEL_OPTIONS } from '~shared/constants/category-carousel-options.constant';
import { ListTruncation, Tag } from '~app/modules/filters/models';
import { select, Store } from '@ngrx/store';
import * as fromFilters from '~app/modules/filters/store';
import { FiltersService } from '~app/modules/filters/services/filters.service';
import { FilterTagService } from '~shared/services/filter-tag.service';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { takeUntil } from 'rxjs/operators';
import { CategoryImagesUtil } from '~app/modules/catalog/utils/category-images.util';
import {
    IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_HEIGHT,
    IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_WIDTH
} from '~app/modules/catalog/constants';
import { CategorySelectedApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationCategorySelectedEventType } from '~shared/modules/application-event/constants';
import { getSecondLevelCategoriesBrand } from '~app/modules/brand-plp/store';
@Component({
    selector: 'app-category-facets-carousel',
    templateUrl: './category-facets-carousel.component.html',
    styleUrls: ['./category-facets-carousel.component.scss'],
})
export class CategoryFacetsCarouselComponent implements OnInit, OnChanges, AfterViewChecked {
    @Input() globalFilter: boolean;
    @Input() isBrandPLP: boolean = false;

    categories$: Observable<Category[]>;
    categoriesList: Category[];
    selectedCategories: Category[];
    defaultCategoryImage = `${environment.cdnBaseUrl()}/images/product-catalog-placeholder.png`;
    customCarouselOptions = CATEGORY_CAROUSEL_OPTIONS;
    limitItems = ListTruncation.maxCategoriesCount;
    hasTruncation = false;
    isExpanded = false;
    shouldShowRightArrow: boolean;
    shouldShowLeftArrow: boolean;

    @ViewChild('container') container: ElementRef;
    @ViewChild('carousel') carousel: ElementRef;
    @ViewChild('carouselInner') carouselInner: ElementRef;

    private unsubscribe$: Subject<void> = new Subject<void>();

    constructor(
        private store: Store<fromFilters.State>,
        private brandInfoStore: Store,
        private filtersService: FiltersService,
        private filterTagsService: FilterTagService,
        private applicationEventService: ApplicationEventService,
        private cd: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.filterTagsService.categoriesTags.subscribe((selectedCategories: Tag<Category>[]) => {
            this.selectedCategories = selectedCategories.map(
                (selectedCategoryTag: Tag<Category>) => selectedCategoryTag?.value
            );
        });

        if (!this.isBrandPLP) {
          this.categories$ = this.store.pipe(select(fromFilters.getCategories));
        } else {
          this.categories$ = this.brandInfoStore.pipe(select(getSecondLevelCategoriesBrand));
        }
        this.categories$.pipe(takeUntil(this.unsubscribe$)).subscribe((categories: Category[]) => {
          this.categoriesList = categories;
          this.hasTruncation = ListTruncation.maxCategoriesCount < this.categoriesList.length;

          if (this.selectedCategories && this.selectedCategories?.length) {
            this.categoriesList = [
              this.selectedCategories[0],
              ...this.categoriesList.filter((category: Category) => category?.id !== this.selectedCategories[0]?.id)
            ];
          }

          this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
          this.shouldShowRightArrow = this.getShouldShowRightArrow();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
        this.shouldShowRightArrow = this.getShouldShowRightArrow();
        this.carousel?.nativeElement?.scrollTo({
            left: 0,
            behaviour: 'smooth'
        });
        this.cd.detectChanges();
    }

    ngAfterViewChecked(): void {
        this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
        this.shouldShowRightArrow = this.getShouldShowRightArrow();
        this.cd.detectChanges();
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private toggleShowList(currentListLength): void {
        this.limitItems =
            currentListLength === ListTruncation.maxCategoriesCount
                ? this.categoriesList.length
                : ListTruncation.maxCategoriesCount;
        this.isExpanded = currentListLength < this.limitItems;
    }

    isCategorySelected(categoryId: string): boolean {
        return !!this.selectedCategories.find((category: Category) => category?.id === categoryId);
    }

    getSource(categoryId: string): string {
        return CategoryImagesUtil.getCategoryImageSrc(
            categoryId,
            IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_HEIGHT,
            IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_WIDTH
        );
    }

    getSourceSet(categoryId: string): string {
        return CategoryImagesUtil.getCategoryImageSrcSet(
            categoryId,
            IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_HEIGHT,
            IMGIX_CATEGORY_FILTER_SEARCH_RESULT_PAGE_IMAGE_WIDTH
        );
    }

    applyCategory(selectedCategory: Category): void {
        if (this.isCategorySelected(selectedCategory?.id)) {
            this.removeCategoryFilter(selectedCategory);
        } else {
            this.filterPlPByCategory([selectedCategory]);
        }

        this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
        this.shouldShowRightArrow = this.getShouldShowRightArrow();
    }

    filterPlPByCategory(category: Category[]): void {
        this.filtersService.filterByCategories(category);
        this.firePLPCategoryTreeClickEvent(category[0]);
    }

    removeCategoryFilter(category: Category) {
        this.filterTagsService.removeCategoryTag(category.slug, category.id);
    }

    public onLeftClick() {
        this.carousel?.nativeElement.scrollTo({
            left: this.carousel?.nativeElement.scrollLeft - 100,
            behaviour: 'smooth'
        });
    }

    public onRightClick() {
        this.carousel?.nativeElement.scrollTo({
            left: this.carousel?.nativeElement.scrollLeft + 100,
            behaviour: 'smooth'
        });
        this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
        this.shouldShowRightArrow = this.getShouldShowRightArrow();
        this.cd.detectChanges();
    }

    public onScrolled() {
        this.shouldShowLeftArrow = this.getShouldShowLeftArrow();
        this.shouldShowRightArrow = this.getShouldShowRightArrow();
        this.cd.detectChanges();
    }

    private firePLPCategoryTreeClickEvent(category: Category): void {
        this.applicationEventService.publish(
            new CategorySelectedApplicationEvent(ApplicationCategorySelectedEventType.CategoryFilterPLP, category.name)
        );
    }

    private getShouldShowLeftArrow(): boolean {
        const carousel = this.container?.nativeElement;
        const carouselInner = this.carouselInner?.nativeElement;

        const contenedorRect = carousel?.getBoundingClientRect();
        const elementoRect = carouselInner?.getBoundingClientRect();

        return Math.floor(elementoRect?.left) < Math.floor(contenedorRect?.left);
    }

    private getShouldShowRightArrow(): boolean {
        const container = this.carousel?.nativeElement;
        const innerCarousel = this.carouselInner?.nativeElement;

        const contenedorRect = container?.getBoundingClientRect();
        const elementoRect = innerCarousel?.getBoundingClientRect();

        return Math.floor(elementoRect?.right) > Math.floor(contenedorRect?.right);
    }
}
